import {
  ExpiryType,
  formatDateToRequestDay,
  getFormatter,
  InstrumentCategoryEnum,
  InstrumentStoreItem,
  InstrumentStorePrice,
  InventoryManagementMethod,
  isDefined,
  isListedInstrumentTradableInApp,
  isMarketOrderType,
  ListedInstrument,
  Loadable,
  LoadableState,
  OrderType,
  orderTypesWhenPriceRequired,
  orderTypesWhenStopActivationPriceRequired,
  TradeType,
  TradingSession,
  useDictionary,
  useInstrumentStoreItemSelector,
  useSubscribeToInstrumentChanges,
} from '@eih-netbroker/common';
import { Accordion, AccordionDetails, AccordionSummary, Collapse } from '@mui/material';
import { isWeekend } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as ArrowDown } from '../../../../assets/icons/arrow-down-s-line.svg';
import { ReactComponent as ContentCopyIcon } from '../../../../assets/icons/copy.svg';
import { ReactComponent as ErrorIcon } from '../../../../assets/icons/error-warning-fill.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { NbDatePicker } from '../../../../shared/components/date-picker/DatePicker';
import { NbIconButton } from '../../../../shared/components/icon-button/IconButton';
import { NbIndefiniteLoaderValueWrapper } from '../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { NbNumericFormat } from '../../../../shared/components/numeric-format/numericFormat';
import { NbSelect } from '../../../../shared/components/select/Select';
import { TradeTypeSwitch } from '../../../../shared/components/switch/TradeTypeSwitch';
import { NbTextField } from '../../../../shared/components/text-field/TextField';
import textFieldClasses from '../../../../shared/components/text-field/TextField.module.scss';
import { NbTooltip } from '../../../../shared/components/tooltip/Tooltip';
import { instrumentStore } from '../../../../shared/data/instrumentStore';
import { useEStream } from '../../../../shared/services/sse/useEStream';
import { useNonTradingDays } from '../../../../shared/services/useNonTradingDays';
import { useOptions } from '../../../../shared/services/useOptions';
import { featureFlagUtils } from '../../../../shared/utils/FeatureFlagUtils';
import { cssWhen } from '../../../../shared/utils/themeUtils';
import { useDebounce } from '../../../../shared/utils/useDebounce';
import { useLogger } from '../../../../shared/utils/useLogger';
import { useFuturesInfo } from '../../services/order/useFuturesInfo';
import { useCreateNormalOrder, ValidatedOrder } from '../../services/order/useNormalOrderView';
import { useOrderExpiryType } from '../../services/order/useOrderExpiryType';
import { useOrderTradingSession } from '../../services/order/useOrderTradingSession';
import { useOrderState } from '../OrderStateContext';
import { WidgetInstrumentSearchAndDisplay } from '../WidgetInstrumentSearchAndDisplay';
import { OrderExchange } from './OrderExchange';
import classes from './OrderForm.module.scss';
import { OrderTransactionInfo } from './OrderTranscationInfoAccordion';

export type OrderCurrencyExchangeParams = {
  sourceAmount?: number;
  targetAmount: number;
  exchangeRate?: number;
  sourceCurrency: string;
};

export type OrderFormData = {
  instrument?: ListedInstrument | null;
  orderType?: OrderType;
  tradeType?: TradeType;
  quantity?: number;
  price?: number;
  stopActivationPrice?: number;
  expiryType?: ExpiryType;
  expiry?: Date;
  inventoryManagementMethod?: InventoryManagementMethod | null;
  tradingSession?: TradingSession;
  currencyExchangeParams?: 'initialized' | OrderCurrencyExchangeParams;
};

export type PriceValidationState = {
  loading?: boolean;
  errorMessage?: string;
};

interface OrderFormProps {
  order?: OrderFormData;
  widgetGroupId?: string | number;
  onChange?: (order: OrderFormData) => void;
  onExchangeParamsChange: (currencyExchangeParams: OrderCurrencyExchangeParams | undefined) => void;
  onIsExchangeEnabledChange: (isEnabled: boolean) => void;
  onInstrumentChange: (instrument: ListedInstrument | null) => void;
  priceValidationState?: PriceValidationState;
  stopActivationPriceValidationState?: PriceValidationState;
  isModify?: boolean;
  transactionId?: string;
  isLoading: boolean;
  isWidget?: boolean;
  initialOrder: ValidatedOrder | null;
  isOrderFormDetailsExpanded?: boolean;
  isExchangeEnabled: boolean;
}

const formatSelectOptionTestIdValue = (value: unknown): string => String(value).trim().toLowerCase();
const unknownValue = '-';

const selectPrice = (item: InstrumentStoreItem | null) => item?.price;
const priceEqual = (a?: InstrumentStorePrice, b?: InstrumentStorePrice) => a?.value === b?.value && a?.type === b?.type;

export const OrderForm = ({
  widgetGroupId,
  order = {},
  onChange,
  onInstrumentChange,
  priceValidationState,
  stopActivationPriceValidationState,
  isModify,
  transactionId,
  isLoading,
  isWidget,
  initialOrder,
  onExchangeParamsChange,
  onIsExchangeEnabledChange,
  isOrderFormDetailsExpanded,
  isExchangeEnabled,
}: OrderFormProps) => {
  const { translation, translateWithParams } = useLocale();
  const { inventoryManagementMethodOptions, getExpiryTypeOptions, getTradingSessionOptions, getOrderTypeOptions } =
    useOptions();
  const { getMaxQuantity } = useCreateNormalOrder();
  const { formatCurrency } = getFormatter();
  const [instrumentData, setInstrumentData] = useState<InstrumentStoreItem | null>(null);
  const { subscribeToInstrumentChanges } = useEStream();
  const { getOrderTotalPrice, availableBalance, getFuturesOrderTransactionInfo } = useOrderState();
  const { formatDictionaryType } = useDictionary();
  const { futuresInitialMargin } = useFuturesInfo();
  const { debounce, cancelDebounce } = useDebounce<void>(() => {}, 500);
  const isMarketOrder = isMarketOrderType(order.orderType);
  const livePrice = useInstrumentStoreItemSelector(
    order.instrument ?? null,
    instrumentStore,
    subscribeToInstrumentChanges,
    selectPrice,
    priceEqual,
    { source: 'OrderFormOld' },
  );
  const effectivePrice = useMemo(
    () => (isMarketOrder ? livePrice?.value : order.price),
    [isMarketOrder, livePrice, order.price],
  );
  const logger = useLogger('OrderFromOld');

  const [isOrderFormDetailsOpen, setIsOrderFormDetailsOpen] = useState(isOrderFormDetailsExpanded);

  const { getNonTradingDaysByMic, getNextTradingDay } = useNonTradingDays();
  const { getInitialTradingSession } = useOrderTradingSession();
  const { getDefaultExpiryDate } = useOrderExpiryType();
  const lastGtdExpiryRef = useRef<Date | undefined>(undefined);

  const nextTradingDay = useMemo(
    () => (order.instrument?.mic ? getNextTradingDay(order.instrument.mic) : undefined),
    [order.instrument, getNextTradingDay],
  );

  useSubscribeToInstrumentChanges({
    source: `order-form-${isWidget ? widgetGroupId : 'modal'}`,
    instrument: order.instrument,
    onChange: setInstrumentData,
    subscribeToInstrumentChanges,
  });

  useEffect(() => {
    setInstrumentData(null);
  }, [order.instrument, setInstrumentData]);

  useEffect(() => {
    setIsOrderFormDetailsOpen(isOrderFormDetailsExpanded);
  }, [isOrderFormDetailsExpanded]);

  const handleChange = <T extends keyof OrderFormData>(key: T, value: OrderFormData[T]) => {
    if (key === 'orderType') {
      const nextOrderType = value as OrderType;
      const tradingSession = getInitialTradingSession(order.instrument, nextOrderType);
      onChange?.({
        ...order,
        tradingSession,
        [key]: value,
        price: value !== OrderType.StopLimit && value !== OrderType.Limit ? undefined : order.price,
        stopActivationPrice:
          value !== OrderType.StopLimit && value !== OrderType.StopMarket ? undefined : order.stopActivationPrice,
      });
    } else if (key === 'expiryType') {
      const expiry =
        value === ExpiryType.Gtd
          ? lastGtdExpiryRef.current ?? getDefaultExpiryDate(order.instrument, value as ExpiryType)
          : undefined;
      onChange?.({
        ...order,
        [key]: value,
        expiry,
      });
    } else if (key === 'expiry') {
      lastGtdExpiryRef.current = value as Date | undefined;
      onChange?.({ ...order, [key]: value });
    } else {
      onChange?.({ ...order, [key]: value });
    }
  };

  const shouldDisableDate = useCallback(
    (day: Date) => {
      if (!order.instrument) {
        return false;
      } else {
        const days = new Set(getNonTradingDaysByMic(order.instrument.mic));
        return isWeekend(day) || days.has(formatDateToRequestDay(day));
      }
    },
    [getNonTradingDaysByMic, order],
  );

  const stopLimitPriceDisabled = order.orderType ? !orderTypesWhenPriceRequired.includes(order.orderType) : true;

  const [transactionValue, setTransactionValue] = useState<LoadableState<string>>(Loadable.uninitialized);
  const [futuresValue, setFuturesValue] = useState<
    LoadableState<{ marginRequirementSum: string; netValueSum: string }>
  >(Loadable.uninitialized);

  const updateTransactionValue = useCallback(async () => {
    if (
      !order.instrument ||
      !order.quantity ||
      !order.orderType ||
      (!effectivePrice && orderTypesWhenPriceRequired.includes(order.orderType))
    ) {
      setTransactionValue(Loadable.loaded(unknownValue));
      setFuturesValue(Loadable.loaded({ marginRequirementSum: unknownValue, netValueSum: unknownValue }));
      return;
    }

    if (order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
      setFuturesValue(Loadable.loading);
      try {
        const futuresInfo = await getFuturesOrderTransactionInfo({
          quantity: order.quantity,
          instrument: order.instrument,
          orderType: order.orderType,
          price: effectivePrice,
        });

        if (!isDefined(futuresInfo)) {
          return Loadable.loading;
        }

        setFuturesValue(
          Loadable.loaded({
            marginRequirementSum: formatCurrency(futuresInfo.marginRequirementSum, order.instrument),
            netValueSum: formatCurrency(futuresInfo.netValueSum, order.instrument),
          }),
        );
      } catch (error) {
        logger.error('Failed to fetch futures order transaction info', error);
        setFuturesValue(Loadable.loaded({ marginRequirementSum: unknownValue, netValueSum: unknownValue }));
      }
    } else {
      setTransactionValue(Loadable.loading);
      try {
        const transactionInfo = await getOrderTotalPrice({
          quantity: order.quantity,
          instrument: order.instrument,
          orderType: order.orderType,
          price: effectivePrice,
        });
        setTransactionValue(
          Loadable.loaded({
            value: formatCurrency(transactionInfo, order.instrument),
          }),
        );
      } catch (error) {
        logger.error('Failed to fetch order total price', error);
        setTransactionValue(Loadable.loaded(unknownValue));
      }
    }
  }, [
    order.instrument,
    order.quantity,
    order.orderType,
    effectivePrice,
    formatCurrency,
    getOrderTotalPrice,
    getFuturesOrderTransactionInfo,
    logger,
  ]);

  const prevOrderTypeRef = useRef(order.orderType);

  useEffect(() => {
    const orderTypeChanged = prevOrderTypeRef.current !== order.orderType;
    prevOrderTypeRef.current = order.orderType;

    cancelDebounce();

    if (!isMarketOrder || orderTypeChanged) {
      updateTransactionValue();
    } else {
      debounce(async () => {
        await updateTransactionValue();
      });
    }
  }, [updateTransactionValue, cancelDebounce, debounce, isMarketOrder, order.orderType]);

  const stopActivationPriceDisabled = order.orderType
    ? !orderTypesWhenStopActivationPriceRequired.includes(order.orderType)
    : true;

  const tradeable = isListedInstrumentTradableInApp(order?.instrument);

  const handleOnMaxClick = useCallback(async () => {
    if (
      order.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures &&
      availableBalance.isLoaded &&
      futuresInitialMargin.isLoaded
    ) {
      if (futuresInitialMargin.futuresInitialMargin === 0) {
        return;
      }

      const quantity = Math.floor(availableBalance.value / futuresInitialMargin.futuresInitialMargin);
      onChange?.({ ...order, quantity });
      return;
    }

    const quantity = await getMaxQuantity(order);
    onChange?.({ ...order, quantity });
  }, [order, availableBalance, futuresInitialMargin, getMaxQuantity, onChange]);

  const updateIsExchangeEnabled = (value: boolean) => {
    onIsExchangeEnabledChange(value);
    if (value) {
      if (isOrderFormDetailsOpen) {
        setIsOrderFormDetailsOpen(false);
      }
      handleChange('currencyExchangeParams', 'initialized');
      //   {
      //   sourceAmount: 0,
      //   targetAmount: 0,
      //   exchangeRate: 0,
      //   sourceCurrency: order.instrument?.currency ?? '',
      // });
    } else {
      if (!isOrderFormDetailsOpen) {
        setIsOrderFormDetailsOpen(true);
      }
    }
  };

  const orderFormAccordion = useMemo(() => {
    if (!isOrderFormDetailsExpanded) {
      return (
        <div className={classes.orderFormAccordion}>
          {translation.orderModal.orderDetails}

          <div className={classes.orderFormAccordionRight}>
            {order.orderType && <span>{formatDictionaryType(order.orderType, 'orderTypes')}</span>}
            {order.tradeType && <span>{translation.tradeTypeOptions[order.tradeType]}</span>}
            {isDefined(order.quantity) && (
              <span>
                {order.quantity} {translation.orderModal.quantityEndorment}
              </span>
            )}
          </div>
        </div>
      );
    }

    return translation.orderModal.orderDetails;
  }, [formatDictionaryType, isOrderFormDetailsExpanded, order, translation]);

  return (
    <div className={cssWhen(classes.orderForm, [!isWidget, classes.orderFormIsModal])}>
      <div className={`${classes.orderFormContent} ${isWidget ? classes.orderFormContentIsWidget : ''}`}>
        <div className={classes.orderFormRow}>
          <WidgetInstrumentSearchAndDisplay
            transactionId={transactionId}
            disabled={isModify}
            source={`order-form-search-${isWidget ? widgetGroupId : 'modal'}`}
            instrument={order?.instrument ?? null}
            updateInstrument={onInstrumentChange}
            readOnly={isLoading}
            dataTestId="order-instrument-search-input"
          />
          {order?.instrument && tradeable === false && (
            <NbTooltip title={translation.orderbook.nonTradeableInstrument}>
              <span>
                <NbIconButton
                  icon={<ErrorIcon />}
                  sx={{ fill: classes.iconColor }}
                />
              </span>
            </NbTooltip>
          )}
        </div>
      </div>

      <OrderTransactionInfo
        instrument={order.instrument}
        className={classes.orderFormRowTransactionInfo}
      />

      <Accordion
        disableGutters
        expanded={isOrderFormDetailsOpen}
        onChange={() => setIsOrderFormDetailsOpen(prev => !prev)}
        className={classes.orderFormDetailsAccordion}
      >
        <AccordionSummary
          className={classes.orderFormTitle}
          expandIcon={<ArrowDown />}
          data-testid="order-form-order-details-button"
        >
          {orderFormAccordion}
        </AccordionSummary>
        <AccordionDetails className={classes.orderDetails}>
          <div
            className={cssWhen(classes.orderFormContent, classes.orderFormContentIsDetails, [
              isWidget,
              classes.orderFormContentIsWidget,
            ])}
          >
            <div className={classes.orderFormRow}>
              {!isWidget && (
                <p
                  className={cssWhen(classes.orderFormRowTitle, [
                    tradeable === false,
                    classes.orderFormRowTitleIsDisabled,
                  ])}
                >
                  {translation.orderModal.transactionType}
                </p>
              )}

              <div className={`${classes.orderFormRowButtons} ${isWidget ? classes.orderFormRowButtonsIsWidget : ''}`}>
                <TradeTypeSwitch
                  disabled={isModify}
                  left={{
                    name: translation.orderModal.sell,
                    value: TradeType.Sell,
                  }}
                  right={{
                    name: translation.orderModal.buy,
                    value: TradeType.Buy,
                  }}
                  onChange={value => handleChange('tradeType', value)}
                  selectedValue={order.tradeType}
                  readOnly={isLoading}
                />
              </div>
            </div>

            <div
              className={cssWhen(classes.orderFormRow, [
                !order.instrument || tradeable === false,
                classes.orderFormRowIsDisabled,
              ])}
            >
              <p
                className={`${classes.orderFormRowTitle} `}
                data-testid="order-form-order-type-label"
              >
                {translation.orderModal.orderType}
              </p>
              <div className={classes.orderFormRowSelect}>
                <NbSelect
                  size={'small'}
                  options={getOrderTypeOptions(order.instrument)}
                  value={order?.orderType ?? ''}
                  onChange={value => handleChange('orderType', value.target.value as OrderType)}
                  disabled={!order.instrument || tradeable === false || isModify}
                  inputProps={{ 'data-testid': 'order-form-order-type-input' }}
                  MenuProps={{
                    MenuListProps: {
                      'data-testid': 'order-form-order-type-list',
                    },
                  }}
                  getOptionTestId={option => `order-form-order-type-option-${String(option.id).toLowerCase()}`}
                  readOnly={isLoading}
                />
              </div>
            </div>

            <div className={cssWhen(classes.orderFormRow, [tradeable === false, classes.orderFormRowIsDisabled])}>
              <p
                className={classes.orderFormRowTitle}
                data-testid="order-form-quantity-label"
              >
                {translation.orderModal.quantity}
              </p>
              <div className={classes.orderFormRowInputs}>
                <NbNumericFormat
                  value={order.quantity ?? ''}
                  decimalScale={0}
                  size="small"
                  onValueChange={e => handleChange('quantity', e.floatValue)}
                  InputProps={{
                    className: classes.orderFormRowQuantity,
                    readOnly: isLoading,
                    endAdornment: (
                      <span
                        className={`${textFieldClasses.nbTextFieldAdornment} ${textFieldClasses.nbTextFieldAdornmentIsEnd}`}
                      >
                        {translation.orderModal.quantityEndorment}
                      </span>
                    ),
                  }}
                  inputProps={{ 'data-testid': 'order-form-quantity-input' }}
                  customInput={NbTextField}
                  disabled={tradeable === false}
                  readOnly={isLoading}
                />

                <NbButton
                  color="emphasize"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleOnMaxClick}
                  disabled={isLoading || tradeable === false || !order.instrument}
                >
                  {translation.orderModal.max}
                </NbButton>
              </div>
            </div>

            {order.instrument?.instrumentCategory !== InstrumentCategoryEnum.Futures && (
              <div className={classes.orderFormRow}>
                <p className={classes.orderFormRowTitle}>{''}</p>

                <span className={classes.orderFormRowText}>
                  <NbIndefiniteLoaderValueWrapper
                    loadableValue={transactionValue}
                    loadedValueCb={loaded =>
                      translateWithParams(translation.orderModal.transactionValue, {
                        value: loaded.value,
                      })
                    }
                  />
                </span>
              </div>
            )}

            {order.instrument?.instrumentCategory === InstrumentCategoryEnum.Futures && (
              <div className={classes.orderFormRow}>
                <p className={classes.orderFormRowTitle}>{''}</p>

                <span className={classes.orderFormRowText}>
                  <NbIndefiniteLoaderValueWrapper
                    loadableValue={futuresValue}
                    loadedValueCb={loaded => (
                      <>
                        {translateWithParams(translation.orderModal.futuresMarginRequirementSum, {
                          value: loaded.marginRequirementSum,
                        })}
                        <br />
                        {translateWithParams(translation.orderModal.futuresNetValue, {
                          value: loaded.netValueSum,
                        })}
                      </>
                    )}
                  />
                </span>
              </div>
            )}

            <div
              className={cssWhen(classes.orderFormRow, [
                stopLimitPriceDisabled || tradeable === false,
                classes.orderFormRowIsDisabled,
              ])}
            >
              <p
                className={classes.orderFormRowTitle}
                data-testid="order-form-limit-price-label"
              >
                {translation.orderModal.limit}
              </p>
              <div className={classes.orderFormRowInput}>
                <NbNumericFormat
                  isLoading={priceValidationState?.loading}
                  errorMessage={priceValidationState?.errorMessage}
                  disabled={stopLimitPriceDisabled || tradeable === false}
                  value={order.price ?? ''}
                  onValueChange={e => handleChange('price', e.floatValue)}
                  size="small"
                  inputProps={{ 'data-testid': 'order-form-limit-price-input' }}
                  customInput={NbTextField}
                  readOnly={isLoading}
                />
              </div>
            </div>

            <div
              className={cssWhen(classes.orderFormRow, [
                stopActivationPriceDisabled || tradeable === false,
                classes.orderFormRowIsDisabled,
              ])}
            >
              <p
                className={classes.orderFormRowTitle}
                data-testid="order-form-stop-price-label"
              >
                {translation.orderModal.stopPrice}
              </p>
              <div className={classes.orderFormRowInput}>
                <NbNumericFormat
                  isLoading={stopActivationPriceValidationState?.loading}
                  errorMessage={stopActivationPriceValidationState?.errorMessage}
                  disabled={stopActivationPriceDisabled}
                  size="small"
                  onValueChange={e => handleChange('stopActivationPrice', e.floatValue)}
                  value={order.stopActivationPrice ?? ''}
                  inputProps={{ 'data-testid': 'order-form-stop-price-input' }}
                  customInput={NbTextField}
                  readOnly={isLoading}
                />
              </div>
            </div>

            <div
              className={cssWhen(classes.orderFormRow, [
                !order.instrument || tradeable === false,
                classes.orderFormRowIsDisabled,
              ])}
            >
              <p
                className={classes.orderFormRowTitle}
                data-testid="order-form-time-in-force-label"
              >
                {translation.orderModal.timeInForce}
              </p>
              <div className={classes.orderFormRowSelect}>
                <NbSelect
                  size="small"
                  onChange={e => handleChange('expiryType', e.target.value as ExpiryType)}
                  value={order.expiryType ?? ''}
                  options={getExpiryTypeOptions(order.instrument, order.orderType)}
                  disabled={!order.instrument || tradeable === false}
                  inputProps={{ 'data-testid': 'order-form-time-in-force-input' }}
                  MenuProps={{
                    MenuListProps: {
                      'data-testid': 'order-form-time-in-force-list',
                    },
                  }}
                  getOptionTestId={option =>
                    `order-form-time-in-force-option-${formatSelectOptionTestIdValue(option.id)}`
                  }
                  readOnly={isLoading}
                />
              </div>
            </div>

            <Collapse
              classes={{
                entered: classes.orderFormRow,
              }}
              in={!!order.instrument && order.expiryType === ExpiryType.Gtd}
            >
              <div className={classes.orderFormRow}>
                <p
                  className={`${classes.orderFormRowTitle} ${
                    tradeable === false ? classes.orderFormRowTitleIsDisabled : ''
                  }`}
                >
                  {''}
                </p>
                <div className={classes.orderFormRowSelect}>
                  <NbDatePicker
                    shouldDisableDate={shouldDisableDate}
                    size="small"
                    onChange={date => handleChange('expiry', date ?? undefined)}
                    value={order.expiry ?? null}
                    minDate={nextTradingDay}
                    disabled={!order.instrument || tradeable === false}
                    readOnly={isLoading}
                  />
                </div>
              </div>
            </Collapse>

            <div
              className={cssWhen(classes.orderFormRow, [
                order.tradeType !== TradeType.Sell || tradeable === false,
                classes.orderFormRowIsDisabled,
              ])}
            >
              <p
                className={classes.orderFormRowTitle}
                data-testid="order-form-stock-valuation-label"
              >
                {translation.orderModal.stockValuation}
              </p>
              <div className={classes.orderFormRowSelect}>
                <NbSelect
                  disabled={order.tradeType !== TradeType.Sell || tradeable === false}
                  size={'small'}
                  onChange={e => handleChange('inventoryManagementMethod', e.target.value as InventoryManagementMethod)}
                  value={order.inventoryManagementMethod ?? ''}
                  options={inventoryManagementMethodOptions}
                  inputProps={{ 'data-testid': 'order-form-stock-valuation-input' }}
                  MenuProps={{
                    MenuListProps: {
                      'data-testid': 'order-form-stock-valuation-list',
                    },
                  }}
                  getOptionTestId={option =>
                    `order-form-stock-valuation-option-${formatSelectOptionTestIdValue(option.id)}`
                  }
                  readOnly={isLoading}
                />
              </div>
            </div>

            <div
              className={cssWhen(classes.orderFormRow, [
                !order.instrument || tradeable === false,
                classes.orderFormRowIsDisabled,
              ])}
            >
              <p
                className={classes.orderFormRowTitle}
                data-testid="order-form-trading-session-label"
              >
                {translation.orderModal.tradingRestrictions}
              </p>
              <div className={classes.orderFormRowSelect}>
                <NbSelect
                  disabled={!order.instrument || tradeable === false}
                  onChange={e => handleChange('tradingSession', e.target.value as TradingSession)}
                  size={'small'}
                  options={getTradingSessionOptions(order.instrument, order.orderType)}
                  value={order.tradingSession ?? ''}
                  inputProps={{ 'data-testid': 'order-form-trading-session-input' }}
                  MenuProps={{
                    MenuListProps: {
                      'data-testid': 'order-form-trading-session-list',
                    },
                  }}
                  getOptionTestId={option =>
                    `order-form-trading-session-option-${formatSelectOptionTestIdValue(option.id)}`
                  }
                  readOnly={isLoading}
                />
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      {featureFlagUtils.isOrderCurrencyExchangeEnabled &&
        order.instrument?.instrumentCategory !== InstrumentCategoryEnum.Futures && (
          <OrderExchange
            initialOrder={initialOrder}
            isExchangeEnabled={isExchangeEnabled}
            setIsExchangeEnabled={updateIsExchangeEnabled}
            order={order}
            instrumentData={instrumentData}
            updateOrder={onExchangeParamsChange}
          />
        )}
    </div>
  );
};
